/**
 * Worldpay Integrated 3DS — browser helpers.
 *
 * The backend (/worldpay/pay, /worldpay/3ds/continue) drives the payment and
 * returns the next 3DS step. These helpers render the two iframes the browser
 * must show:
 *   - Device Data Collection (DDC): a hidden iframe that auto-POSTs Bin+JWT to
 *     the issuer; the result arrives via postMessage ("profile.completed").
 *   - Challenge: a visible iframe overlay that auto-POSTs JWT+MD to the issuer;
 *     when the customer finishes, the issuer POSTs to our backend returnUrl,
 *     which posts a completion message back to this window.
 *
 * Security note: the challenge completion message only triggers the next
 * server call — the real authentication outcome is verified server-side via
 * the signed action href, so we don't over-trust the postMessage here.
 */

// Browser/device data sent on the initial /worldpay/pay request. Worldpay uses
// it to decide frictionless vs challenge.
export function buildDeviceData() {
  let javaEnabled = false;
  try {
    javaEnabled =
      typeof navigator.javaEnabled === "function"
        ? navigator.javaEnabled()
        : false;
  } catch (e) {
    javaEnabled = false;
  }
  return {
    acceptHeader: "application/json",
    userAgentHeader: navigator.userAgent || "",
    browserLanguage: navigator.language || "en-GB",
    browserScreenHeight: window.screen ? window.screen.height : 0,
    browserScreenWidth: window.screen ? window.screen.width : 0,
    browserColorDepth: String((window.screen && window.screen.colorDepth) || 24),
    browserJavaEnabled: javaEnabled,
    browserJavascriptEnabled: true,
    timeZone: String(new Date().getTimezoneOffset()),
  };
}

function autoPostSrcdoc(actionUrl, fields) {
  const inputs = Object.keys(fields)
    .map(
      (k) =>
        '<input type="hidden" name="' +
        k +
        '" value="' +
        String(fields[k] == null ? "" : fields[k]).replace(/"/g, "&quot;") +
        '"/>'
    )
    .join("");
  return (
    "<!doctype html><html><head><meta charset=\"utf-8\"></head><body>" +
    '<form id="wpf" method="POST" action="' +
    actionUrl +
    '">' +
    inputs +
    "</form>" +
    "<script>document.getElementById('wpf').submit();</script>" +
    "</body></html>"
  );
}

/**
 * Run Device Data Collection. Resolves with the collectionReference (SessionId)
 * or "" on timeout/failure — Worldpay can continue without it.
 */
export function runDeviceDataCollection(ddc) {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.width = "1";
    iframe.height = "1";
    iframe.title = "worldpay-3ds-ddc";
    iframe.style.cssText = "display:none;border:0;position:absolute;left:-9999px;";
    let done = false;
    let timer = null;
    const cleanup = () => {
      window.removeEventListener("message", onMsg);
      if (timer) clearTimeout(timer);
      try {
        document.body.removeChild(iframe);
      } catch (e) {}
    };
    const finish = (ref) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(ref || "");
    };
    const onMsg = (event) => {
      // DDC postMessage comes from Cardinal (centinelapi[.stag].cardinalcommerce.com).
      if (!event.origin || event.origin.indexOf("cardinalcommerce.com") < 0) {
        return;
      }
      let data = event.data;
      try {
        if (typeof data === "string") data = JSON.parse(data);
      } catch (e) {
        return;
      }
      if (data && data.MessageType === "profile.completed") {
        finish(data.SessionId || "");
      }
    };
    window.addEventListener("message", onMsg);
    iframe.srcdoc = autoPostSrcdoc(ddc.url, { Bin: ddc.bin || "", JWT: ddc.jwt });
    document.body.appendChild(iframe);
    // Spec allows ~10s; proceed without the reference if it doesn't arrive.
    timer = setTimeout(() => finish(""), 12000);
  });
}

/**
 * Display the issuer 3DS challenge in a modal iframe. Resolves when the
 * backend returnUrl page signals completion; rejects on cancel/timeout.
 */
export function runChallenge(challenge) {
  return new Promise((resolve, reject) => {
    const overlay = document.createElement("div");
    overlay.setAttribute("data-worldpay-3ds", "challenge");
    overlay.style.cssText =
      "position:fixed;inset:0;z-index:100000;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;";
    const box = document.createElement("div");
    box.style.cssText =
      "background:#fff;border-radius:10px;padding:10px;box-shadow:0 12px 48px rgba(0,0,0,0.35);";
    const iframe = document.createElement("iframe");
    iframe.title = "worldpay-3ds-challenge";
    iframe.style.cssText =
      "border:0;width:390px;height:400px;max-width:92vw;max-height:80vh;";
    box.appendChild(iframe);
    overlay.appendChild(box);

    let done = false;
    let timer = null;
    const cleanup = () => {
      window.removeEventListener("message", onMsg);
      if (timer) clearTimeout(timer);
      try {
        document.body.removeChild(overlay);
      } catch (e) {}
    };
    const onMsg = (event) => {
      const data = event.data;
      // The returnUrl page lives on the backend origin and posts this message.
      // We key off the payload (origin differs from the app origin); the real
      // outcome is verified server-side, so this only advances the flow.
      if (data && data.source === "worldpay-3ds" && data.status === "complete") {
        if (done) return;
        done = true;
        cleanup();
        resolve();
      }
    };
    window.addEventListener("message", onMsg);
    iframe.srcdoc = autoPostSrcdoc(challenge.url, {
      JWT: challenge.jwt,
      MD: challenge.reference || "",
    });
    document.body.appendChild(overlay);
    // Challenges can take a while; fail safe after 10 minutes.
    timer = setTimeout(() => {
      if (done) return;
      done = true;
      cleanup();
      reject(new Error("3DS challenge timed out"));
    }, 10 * 60 * 1000);
  });
}
